import { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { portfolioEvents } from '../../../mocks/portfolioEvents';

const categories = ['All', 'Wedding', 'Corporate', 'Private Party'];

function BeforeAfterSlider({ before, after, title }: { before: string; after: string; title: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [sliderPos, setSliderPos] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  const updateSlider = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPos(percent);
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    updateSlider(e.clientX);
  }, [updateSlider]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    updateSlider(e.clientX);
  }, [isDragging, updateSlider]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setIsDragging(true);
    updateSlider(e.touches[0].clientX);
  }, [updateSlider]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging) return;
    updateSlider(e.touches[0].clientX);
  }, [isDragging, updateSlider]);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-72 rounded-2xl overflow-hidden cursor-col-resize select-none"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleMouseUp}
    >
      {/* After Image (full background) */}
      <div className="absolute inset-0">
        <img src={after} alt={`${title} after`} className="w-full h-full object-cover object-top" />
      </div>

      {/* Before Image (clipped) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${sliderPos}%` }}
      >
        <img
          src={before}
          alt={`${title} before`}
          className="h-full object-cover object-top"
          style={{ width: containerRef.current ? `${containerRef.current.offsetWidth}px` : '100%', maxWidth: 'none' }}
        />
      </div>

      {/* Slider Line */}
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg z-10"
        style={{ left: `${sliderPos}%`, transform: 'translateX(-50%)' }}
      >
        {/* Slider Handle */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-xl flex items-center justify-center">
          <i className="ri-arrow-left-s-line text-stone-600 text-sm"></i>
          <i className="ri-arrow-right-s-line text-stone-600 text-sm"></i>
        </div>
      </div>

      {/* Labels */}
      <div className="absolute top-3 left-3 z-10">
        <span className="px-3 py-1.5 bg-stone-900/70 backdrop-blur-sm text-white text-xs font-semibold rounded-full">
          Before
        </span>
      </div>
      <div className="absolute top-3 right-3 z-10">
        <span className="px-3 py-1.5 bg-amber-600/80 backdrop-blur-sm text-white text-xs font-semibold rounded-full">
          After
        </span>
      </div>
    </div>
  );
}

export default function Portfolio() {
  const [activeCategory, setActiveCategory] = useState('All');
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ rootMargin: '0px 0px -50px 0px' });

  const filtered = activeCategory === 'All'
    ? portfolioEvents
    : portfolioEvents.filter((item) => item.category === activeCategory);

  const scrollToInquiry = () => {
    const el = document.getElementById('inquiry');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="portfolio" className="py-32 bg-gradient-to-b from-white to-stone-50 relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-200 to-transparent"></div>
      <div className="absolute bottom-40 right-0 w-[500px] h-[500px] bg-amber-50/60 rounded-full blur-3xl -translate-x-1/3"></div>

      <div className="max-w-7xl mx-auto px-6 relative">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-16 scroll-animate ${headerVisible ? 'visible' : ''}`}
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-amber-400"></div>
            <span className="text-amber-600 text-xs font-semibold tracking-[0.25em] uppercase">
              Our Work
            </span>
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-amber-400"></div>
          </div>
          <h2 className="text-5xl md:text-6xl font-display font-normal text-stone-900 mb-5">
            Event <span className="italic text-stone-400">Transformations</span>
          </h2>
          <p className="text-stone-500 max-w-2xl mx-auto leading-relaxed">
            Drag the slider to see how we transform ordinary spaces into extraordinary experiences. Every event tells a story.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-14">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? 'bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 text-white shadow-lg shadow-amber-500/20'
                  : 'bg-stone-100 text-stone-500 hover:text-stone-700 hover:bg-stone-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Portfolio Grid */}
        <div
          ref={gridRef}
          className={`grid md:grid-cols-2 lg:grid-cols-3 gap-8 stagger-children ${gridVisible ? 'visible' : ''}`}
        >
          {filtered.map((item) => (
            <div
              key={item.id}
              className="group bg-white rounded-3xl overflow-hidden shadow-sm border border-stone-100 hover:shadow-xl hover:border-amber-100 transition-all duration-500"
            >
              {/* Before/After Slider */}
              <BeforeAfterSlider before={item.before} after={item.after} title={item.title} />

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-3 py-1 bg-amber-50 text-amber-600 text-xs font-semibold rounded-full">
                    {item.category}
                  </span>
                  <span className="text-stone-400 text-xs flex items-center gap-1">
                    <i className="ri-map-pin-2-line"></i>
                    {item.location.split(',')[1]?.trim()}
                  </span>
                </div>

                <h3 className="text-lg font-serif font-semibold text-stone-900 mb-2 group-hover:text-amber-700 transition-colors">
                  {item.title}
                </h3>

                <div className="flex items-center gap-4 text-sm text-stone-400">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-map-pin-line text-amber-400"></i>
                    {item.location.split(',')[0]}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-group-line text-amber-400"></i>
                    {item.guests} guests
                  </span>
                </div>

                {/* View Full Project Link */}
                <Link
                  to={`/event/${item.slug}`}
                  className="mt-4 inline-flex items-center gap-2 text-amber-600 text-sm font-medium cursor-pointer hover:text-amber-700 transition-colors group/link"
                >
                  <span>View full project</span>
                  <i className="ri-arrow-right-line transition-transform group-hover/link:translate-x-1"></i>
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Row */}
        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { value: '500+', label: 'Events Styled', icon: 'ri-calendar-check-line' },
            { value: '98%', label: 'Client Satisfaction', icon: 'ri-heart-line' },
            { value: '12+', label: 'Years Experience', icon: 'ri-award-line' },
            { value: '50+', label: 'Venue Partners', icon: 'ri-building-line' },
          ].map((stat, idx) => (
            <div key={idx} className="text-center p-6 bg-white rounded-2xl border border-stone-100 shadow-sm">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-amber-50 flex items-center justify-center">
                <i className={`${stat.icon} text-xl text-amber-600`}></i>
              </div>
              <p className="text-3xl font-display text-stone-900 mb-1">{stat.value}</p>
              <p className="text-stone-400 text-sm">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <p className="text-stone-500 mb-5">Love what you see? Let\u2019s create something extraordinary for your next event.</p>
          <button
            onClick={scrollToInquiry}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 text-white text-sm font-semibold rounded-full hover:from-amber-300 hover:via-amber-400 hover:to-amber-500 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/20"
          >
            <i className="ri-chat-1-line"></i>
            Start Your Transformation
          </button>
        </div>
      </div>
    </section>
  );
}